import { createContext, useCallback, useState } from "react";
import { Bundle, Course, GroupSection, InstructorLink, InvalidInput, Term } from "@models/ISchema";
import { IErrorWarning } from "@models/Errors/ErrorWarning";

export type errorInstructorType = "responsability" | "payment";
export interface IGroup {
  id: string;
  vacancies: number;
  label: string;
  enrollment: any;
  waitList: any;
  curriculum: string;
  program: string;
  course: Course;
  campus?: string;
  openGroup?: boolean;
  clientCode?: string;
  isReferent?: boolean;
  isActive: boolean;
  groupSections?: Array<GroupSection>;
  term: Term;
  groupCapacity?: { retakerCapacity: number };
  enrollmentStats?: { retakerEnrollmentsCount: number };
}

export type IBundleTerm = Pick<Term, "id" | "code">;

export interface ILink {
  code: string;
  codeName?: string;
  color?: string;
  idColor?: number;
  vacancies: number;
}

export interface ISection {
  typeComponent: string;
  sectionId: string;
  componentId: string;
  vacancies: number;
  link: Array<ILink>;
}

export interface ILeague {
  code: string;
  codeName: string;
  clientCode?: string | number;
  color: string;
  groups?: Array<IGroup>;
  course?: Course;
  instructors?: Array<InstructorLink>;
  blocksWithoutInstructorAssigment?: number;
  id: string;
  idColor: number;
  label: string;
  sections: Array<ISection>;
  linkId: string;
  totalBlocks: number;
  bundle?: Bundle;
}

export interface IGroupValue {
  linkId: string;
  group?: IGroup;
  instructor?: InstructorLink;
  blockPercent?: number;
  value?: number;
  teachingHoursPercentage?: number;
  paymentPercentage?: number;
  isPrimary?: boolean;
}
export interface IErrorInstructorBundle {
  error: IErrorWarning & {
    messageAccent?: string;
  };
  link: ILeague;
  item: InvalidInput;
}

export interface IContextVacancies {
  currentLeague: Array<ILeague>;
  setCurrentLeague: (value: Array<ILeague>) => void;
  changeValues: Array<Array<IGroupValue>>;
  setChangeValues: (value: Array<any> | ((prev: Array<any>) => Array<any>)) => void;
  retakerChangeValues: Array<Array<IGroupValue>>;
  setRetakerChangeValues: (value: Array<any> | ((prev: Array<any>) => Array<any>)) => void;
  instructorValues: Array<Array<IGroupValue>>;
  setInstructorValues: (value: Array<Array<IGroupValue>>) => void;
  instructorOrigin: Array<Array<IGroupValue>>;
  setInstructorOrigin: (value: Array<Array<IGroupValue>>) => void;
  errorsInstructors: Array<IErrorInstructorBundle>;
  setErrorsInstructors: (value: Array<IErrorInstructorBundle>) => void;
}

const defaultContext: IContextVacancies = {
  currentLeague: null,
  setCurrentLeague: null,
  changeValues: null,
  setChangeValues: null,
  retakerChangeValues: [],
  setRetakerChangeValues: null,
  instructorValues: [],
  setInstructorValues: null,
  instructorOrigin: [],
  setInstructorOrigin: null,
  errorsInstructors: [],
  setErrorsInstructors: null,
};

export const ContextVacancies = createContext<IContextVacancies>(defaultContext);

export const useContextVacancies = (): IContextVacancies => {
  const [currentLeague, setCurrentLeagueState] = useState(defaultContext.currentLeague);
  const [changeValues, setChangeValuesState] = useState(defaultContext.changeValues);
  const [retakerChangeValues, setRetakerChangeValuesState] = useState<Array<Array<IGroupValue>>>(
    defaultContext.retakerChangeValues,
  );
  const [instructorValues, setInstructorValuesState] = useState<Array<Array<IGroupValue>>>(
    defaultContext.instructorValues,
  );
  const [instructorOrigin, setInstructorOriginState] = useState<Array<Array<IGroupValue>>>(
    defaultContext.instructorOrigin,
  );
  const [errorsInstructors, setErrorsInstructorsState] = useState<Array<IErrorInstructorBundle>>(
    defaultContext.errorsInstructors,
  );

  /**
   * Updates the `currentLeague` state. `null` clears it on purpose: a bundle refetch
   * must not mount the tables with the previous leagues while the new ones load.
   */
  const setCurrentLeague = useCallback((value: Array<ILeague>) => {
    setCurrentLeagueState(value);
  }, []);

  /**
   * Updates the `changeValues` state for vacancy inputs.
   *
   * Accepts either a direct value or a functional updater `(prev) => newValue`.
   * The functional updater form is required by `applyValuesChange` in `Links.tsx` to safely
   * update state when called from within a batched React state update cycle (e.g., inside a
   * `setCountVacancies` updater in `useGroupsInputState`). Without the functional form, any
   * snapshot of `changeValues` read from a ref could be stale if React batches the update
   * before committing the latest render.
   */
  const setChangeValues = useCallback((value: Array<any> | ((prev: Array<any>) => Array<any>)) => {
    setChangeValuesState(prev => {
      const newValue = typeof value === "function" ? value(prev) : value;
      return newValue ?? prev;
    });
  }, []);

  /**
   * Updates the `retakerChangeValues` state for retaker capacity inputs.
   * Same rationale as `setChangeValues` — supports functional updater to avoid stale state
   * in batched React update cycles.
   */
  const setRetakerChangeValues = useCallback(
    (value: Array<any> | ((prev: Array<any>) => Array<any>)) => {
      setRetakerChangeValuesState(prev => {
        const newValue = typeof value === "function" ? value(prev) : value;
        return newValue ?? prev;
      });
    },
    [],
  );

  const setInstructorValues = useCallback((value: Array<Array<IGroupValue>>) => {
    if (value) setInstructorValuesState(value);
  }, []);
  const setInstructorOrigin = useCallback((value: Array<Array<IGroupValue>>) => {
    if (value) setInstructorOriginState(value);
  }, []);

  const setErrorsInstructors = useCallback((value: Array<IErrorInstructorBundle>) => {
    if (value) setErrorsInstructorsState(value);
  }, []);

  return {
    currentLeague,
    setCurrentLeague,
    changeValues,
    setChangeValues,
    retakerChangeValues,
    setRetakerChangeValues,
    instructorValues,
    setInstructorValues,
    instructorOrigin,
    setInstructorOrigin,
    errorsInstructors,
    setErrorsInstructors,
  };
};
